import { useState, useEffect } from 'react';

export const SCRIPT_LOADING_STATUS = {
  IDLE: 'idle',
  READY: 'ready',
  LOADING: 'loading',
  ERROR: 'error',
} as const;

// Create a type for the status values
export type ScriptLoadingStatus = (typeof SCRIPT_LOADING_STATUS)[keyof typeof SCRIPT_LOADING_STATUS];

/**
 * Insert a script tag into the body, and return its loading status.
 *
 * @param {string|boolean|null} src URL to load, false to return an error status,
 *   true to return a success status, or null to return an idle status.
 */
const useScript = (src: string | boolean | null): ScriptLoadingStatus => {
  const [status, setStatus] = useState<ScriptLoadingStatus>(
    src ? SCRIPT_LOADING_STATUS.LOADING : SCRIPT_LOADING_STATUS.IDLE
  );

  useEffect(() => {
    if (src === false) {
      setStatus(SCRIPT_LOADING_STATUS.ERROR);
      return;
    }
    if (src === true) {
      setStatus(SCRIPT_LOADING_STATUS.READY);
      return;
    }
    if (!src) {
      setStatus(SCRIPT_LOADING_STATUS.IDLE);
      return;
    }

    let script = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;

    if (!script) {
      script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.setAttribute('data-status', SCRIPT_LOADING_STATUS.LOADING);

      document.body.appendChild(script);

      const setAttributeFromEvent = (event: Event): void => {
        script?.setAttribute(
          'data-status',
          event.type === 'load' ? SCRIPT_LOADING_STATUS.READY : SCRIPT_LOADING_STATUS.ERROR
        );
      };

      script.addEventListener('load', setAttributeFromEvent);
      script.addEventListener('error', setAttributeFromEvent);
    } else {
      const scriptStatus = script.getAttribute('data-status');
      if (scriptStatus) {
        setStatus(scriptStatus as ScriptLoadingStatus);
      }
    }

    const setStateFromEvent = (event: Event): void => {
      setStatus(event.type === 'load' ? SCRIPT_LOADING_STATUS.READY : SCRIPT_LOADING_STATUS.ERROR);
    };

    script.addEventListener('load', setStateFromEvent);
    script.addEventListener('error', setStateFromEvent);

    return () => {
      if (script) {
        script.removeEventListener('load', setStateFromEvent);
        script.removeEventListener('error', setStateFromEvent);
      }
    };
  }, [src]);

  return status;
};

export default useScript;
